import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchUsersAllPages } from '../../../api/administration/users';
import { useUser } from '../../../context/UserContext';

/**
 * Hook for user selection with debounced search.
 * Search is performed entirely on the server (GET /api/users); all matching **active** pages are
 * merged until the API reports no further results.
 *
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 500)
 * @param {{ pageSize?: number, maxPages?: number }} [options] - Chunk size per request (max 100) and safety cap on pages walked.
 * @returns {Object} users list, loading state, error, currentUser
 */
export function useUserSelector(debounceMs = 500, options = {}) {
  const pageSize = Number.isFinite(Number(options.pageSize))
    ? Math.min(100, Math.max(1, Number(options.pageSize)))
    : 100;
  const maxPages = Number.isFinite(Number(options.maxPages))
    ? Math.min(500, Math.max(1, Number(options.maxPages)))
    : 100;

  const { user: currentUser } = useUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs]);

  const shouldSearch = debouncedSearchTerm.trim().length >= 2;

  const {
    data: users = [],
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: [
      'users',
      'selector-backend-search',
      debouncedSearchTerm.trim(),
      pageSize,
      maxPages,
    ],
    queryFn: () =>
      fetchUsersAllPages(
        {
          search: debouncedSearchTerm.trim(),
          isActive: true,
          sortBy: 'firstName',
          sortOrder: 'asc',
        },
        { pageSize, maxPages },
      ),
    enabled: shouldSearch,
    staleTime: 30 * 1000,
  });

  return {
    users,
    loading,
    error: error?.message ?? error ?? null,
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    currentUser,
  };
}
